import React, { useState } from 'react';
import '../../Styles/BusFareChart.css';

const BusFareChart = () => {
  // Bus stop names from the image
  const busStops = [
    "Central Terminal",
    "Market Street",
    "City Hall",
    "Medical Center",
    "University Ave",
    "Tech Park",
    "Shopping Mall",
    "Suburbs Gate",
    "Highlands",
    "Riverside",
    "Airport Ltc",
    "North Station"
  ];

  // Generate realistic fare matrix based on distances between stops
  const generateFareMatrix = () => {
    const matrix = [];
    
    // Base distances between stops (in km)
    const distances = [
      [0, 2, 3, 5, 7, 9, 11, 13, 15, 17, 20, 22],
      [2, 0, 1, 3, 5, 7, 9, 11, 13, 15, 18, 20],
      [3, 1, 0, 2, 4, 6, 8, 10, 12, 14, 17, 19],
      [5, 3, 2, 0, 2, 4, 6, 8, 10, 12, 15, 17],
      [7, 5, 4, 2, 0, 2, 4, 6, 8, 10, 13, 15],
      [9, 7, 6, 4, 2, 0, 2, 4, 6, 8, 11, 13],
      [11, 9, 8, 6, 4, 2, 0, 2, 4, 6, 9, 11],
      [13, 11, 10, 8, 6, 4, 2, 0, 2, 4, 7, 9],
      [15, 13, 12, 10, 8, 6, 4, 2, 0, 2, 5, 7],
      [17, 15, 14, 12, 10, 8, 6, 4, 2, 0, 3, 5],
      [20, 18, 17, 15, 13, 11, 9, 7, 5, 3, 0, 2],
      [22, 20, 19, 17, 15, 13, 11, 9, 7, 5, 2, 0]
    ];

    // Fare calculation: ₹5 per km base + ₹2 per km additional for longer distances
    for (let i = 0; i < busStops.length; i++) {
      const row = [];
      for (let j = 0; j < busStops.length; j++) {
        if (i === j) {
          row.push('-');
        } else {
          const distance = distances[i][j];
          let fare;
          if (distance <= 5) {
            fare = distance * 5; // Short distance: ₹5/km
          } else if (distance <= 10) {
            fare = 25 + (distance - 5) * 6; // Medium distance: ₹6/km for additional km
          } else {
            fare = 55 + (distance - 10) * 7; // Long distance: ₹7/km for additional km
          }
          row.push(Math.round(fare));
        }
      }
      matrix.push(row);
    }
    return matrix;
  };

  const fareMatrix = generateFareMatrix();
  const [searchFrom, setSearchFrom] = useState('');
  const [searchTo, setSearchTo] = useState('');
  const [selectedFrom, setSelectedFrom] = useState(null);
  const [selectedTo, setSelectedTo] = useState(null);

  // Get fare for specific route
  const getFare = (fromIndex, toIndex) => {
    if (fromIndex >= 0 && toIndex >= 0 && fromIndex < fareMatrix.length && toIndex < fareMatrix.length) {
      return fareMatrix[fromIndex][toIndex];
    }
    return 'N/A';
  };

  // Handle cell hover
  const handleCellHover = (fromIndex, toIndex) => {
    setSelectedFrom(fromIndex);
    setSelectedTo(toIndex);
  };

  // Handle cell leave
  const handleCellLeave = () => {
    setSelectedFrom(null);
    setSelectedTo(null);
  };

  // Get fare class based on amount
  // const getFareClass = (fare) => {
  //   if (fare === '-') return 'same-stop';
  //   const fareValue = parseInt(fare);
  //   if (fareValue <= 30) return 'cheapest';
  //   if (fareValue <= 60) return 'moderate';
  //   return 'expensive';
  // };

  const getFareClass = (rowIndex, colIndex) => {
    if (rowIndex === colIndex) return 'same-stop';
   
    return rowIndex < colIndex ? 'above-diagonal' : 'below-diagonal';
  };

  // Handle search
  const handleSearch = () => {
    const fromIndex = busStops.findIndex(stop => stop === searchFrom);
    const toIndex = busStops.findIndex(stop => stop === searchTo);
    
    if (fromIndex >= 0 && toIndex >= 0) {
      const fare = getFare(fromIndex, toIndex);
      alert(`Fare from "${searchFrom}" to "${searchTo}": ₹${fare}`);
    } else {
      alert('Please select valid stops from the dropdown');
    }
  };

  return (
    <div className="bus-fare-chart-container">
      <header className="chart-header">
        <h1>City Bus Fare Chart</h1>
        <p className="subtitle">Complete fare matrix for all bus stops across the city</p>
        <div className="stats">
          <div className="stat-card">
            <span className="stat-value">{busStops.length}</span>
            <span className="stat-label">Total Stops</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{busStops.length * (busStops.length - 1)}</span>
            <span className="stat-label">Possible Routes</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">₹5/km</span>
            <span className="stat-label">Base Fare</span>
          </div>
        </div>
      </header>

      <div className="search-section">
        <div className="search-container">
          <div className="search-input-group">
            <label htmlFor="from-stop">From Stop</label>
            <select
              id="from-stop"
              value={searchFrom}
              onChange={(e) => setSearchFrom(e.target.value)}
              className="stop-select"
            >
              <option value="">Select departure stop</option>
              {busStops.map((stop, index) => (
                <option key={`from-${index}`} value={stop}>
                  {stop}
                </option>
              ))}
            </select>
          </div>
          <div className="arrow">→</div>
          <div className="search-input-group">
            <label htmlFor="to-stop">To Stop</label>
            <select
              id="to-stop"
              value={searchTo}
              onChange={(e) => setSearchTo(e.target.value)}
              className="stop-select"
            >
              <option value="">Select destination stop</option>
              {busStops.map((stop, index) => (
                <option key={`to-${index}`} value={stop}>
                  {stop}
                </option>
              ))}
            </select>
          </div>
          <button 
            className="search-btn"
            onClick={handleSearch}
            disabled={!searchFrom || !searchTo}
          >
            Get Fare
          </button>
        </div>
        
        {searchFrom && searchTo && (
          <div className="fare-result">
            <h3>Route: {searchFrom} → {searchTo}</h3>
            <div className="fare-amount">
              ₹{getFare(
                busStops.findIndex(stop => stop === searchFrom),
                busStops.findIndex(stop => stop === searchTo)
              )}
            </div>
          </div>
        )}
      </div>

      <div className="matrix-controls">
        <div className="control-group">
          <h3>Fare Matrix Legend</h3>
        </div>
        
        {/* {selectedFrom !== null && selectedTo !== null && (
          <div className="selected-info">
            <div className="selected-route">
              <strong>Selected Route:</strong> {busStops[selectedFrom]} → {busStops[selectedTo]}
            </div>
            <div className="selected-fare">
              <strong>Fare:</strong> ₹{getFare(selectedFrom, selectedTo)}
            </div>
          </div>
        )} */}
      </div>

      <div className="matrix-container">
        <div className="matrix-wrapper">
          {/* Column headers (To stops) */}
          <div className="row-headers">
            <div className="header-cell corner">From \ To</div>
            {busStops.map((stop, index) => (
              <div key={`to-${index}`} className="header-cell to-header">
                <span className="stop-name">{stop}</span>
                <span className="stop-number">({index + 1})</span>
              </div>
            ))}
          </div>

          {/* Fare matrix */}
          {busStops.map((fromStop, rowIndex) => (
            <div key={`row-${rowIndex}`} className="matrix-row">
              <div className="header-cell from-header">
                <span className="stop-name">{fromStop}</span>
                <span className="stop-number">({rowIndex + 1})</span>
              </div>
              {busStops.map((toStop, colIndex) => {
  const fare = fareMatrix[rowIndex][colIndex];
  const fareClass = getFareClass(rowIndex, colIndex); // Pass rowIndex and colIndex
  const isSelected = selectedFrom === rowIndex && selectedTo === colIndex;

  return (
    <div
      key={`cell-${rowIndex}-${colIndex}`}
      className={`fare-cell ${fareClass} ${isSelected ? 'selected' : ''}`}
      onMouseEnter={() => handleCellHover(rowIndex, colIndex)}
      onMouseLeave={handleCellLeave}
      onClick={() => {
        setSearchFrom(fromStop);
        setSearchTo(toStop);
      }}
      title={`${fromStop} → ${toStop}: ₹${fare}`}
    >
      {fare === '-' ? '—' : `₹${fare}`}
    </div>
  );
})}
            </div>
          ))}
        </div>
      </div>

      <div className="instructions">
        <h3>How to Use the Fare Chart</h3>
        <div className="instructions-grid">
          <div className="instruction">
            <div className="instruction-icon">1</div>
            <p><strong>Hover</strong> over any cell to see the fare details</p>
          </div>
          <div className="instruction">
            <div className="instruction-icon">2</div>
            <p><strong>Click</strong> on a cell to auto-fill the search form</p>
          </div>
          <div className="instruction">
            <div className="instruction-icon">3</div>
            <p><strong>Select</strong> stops from the dropdown menus for quick search</p>
          </div>
          <div className="instruction">
            <div className="instruction-icon">4</div>
            <p><strong>Read</strong> the color codes to identify fare ranges</p>
          </div>
        </div>
      </div>

      <div className="stops-list">
        <h3>All Bus Stops</h3>
        <div className="stops-grid">
          {busStops.map((stop, index) => (
            <div key={`list-${index}`} className="stop-item">
              <span className="stop-number">{index + 1}</span>
              <span className="stop-name">{stop}</span>
            </div>
          ))}
        </div>
      </div>

      <footer className="chart-footer">
        <p>Note: Fares are calculated based on distance between stops. ₹5 per km for first 5km, ₹6 per km for 6-10km, ₹7 per km beyond 10km.</p>
        <p>Total bus stops in system: {busStops.length} | Last updated: Today</p>
      </footer>
    </div>
  );
};

export default BusFareChart;